import React, { useRef } from 'react';
import axios from 'axios';
import { Paperclip, X, Loader2, FileText } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const ALLOWED = ['jpg', 'jpeg', 'png', 'webp', 'heic', 'pdf', 'dwg', 'dxf'];
const MAX_FILES = 5;
const MAX_SIZE = 10 * 1024 * 1024;

// files: [{ key, name, status: 'uploading'|'done'|'error', id }]
export const AttachmentPicker = ({ files, setFiles }) => {
  const { t } = useLanguage();
  const inputRef = useRef(null);

  const update = (key, patch) => setFiles((prev) => prev.map((f) => (f.key === key ? { ...f, ...patch } : f)));

  const upload = async (file, key) => {
    const body = new FormData();
    body.append('file', file);
    try {
      const { data } = await axios.post('/api/contact/upload', body);
      update(key, { status: 'done', id: data.id });
    } catch (e) {
      update(key, { status: 'error' });
    }
  };

  const onPick = (e) => {
    const picked = Array.from(e.target.files || []);
    e.target.value = '';
    const room = MAX_FILES - files.length;
    picked.slice(0, Math.max(room, 0)).forEach((file) => {
      const ext = file.name.split('.').pop().toLowerCase();
      const key = `${Date.now()}-${Math.random()}`;
      const bad = !ALLOWED.includes(ext) ? t('contact.attachBadType') : file.size > MAX_SIZE ? t('contact.attachTooBig') : '';
      setFiles((prev) => [...prev, { key, name: file.name, status: bad ? 'error' : 'uploading', error: bad }]);
      if (!bad) upload(file, key);
    });
  };

  return (
    <div data-testid="contact-attachments">
      <label className="block text-gray-900 dark:text-white font-medium mb-2">{t('contact.attachLabel')}</label>
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        disabled={files.length >= MAX_FILES}
        data-testid="contact-attach-btn"
        className="w-full flex items-center justify-center gap-2 border-2 border-dashed border-gray-300 dark:border-gray-700 hover:border-green-500 disabled:opacity-50 rounded-lg px-4 py-4 text-gray-600 dark:text-gray-400 transition-colors duration-200"
      >
        <Paperclip size={18} />
        <span>{t('contact.attachButton')}</span>
      </button>
      <p className="text-xs text-gray-500 mt-1">{t('contact.attachHint')}</p>
      <input ref={inputRef} type="file" multiple hidden accept={ALLOWED.map((x) => `.${x}`).join(',')} onChange={onPick} data-testid="contact-attach-input" />
      {files.length > 0 && (
        <ul className="mt-3 space-y-2">
          {files.map((f) => (
            <li key={f.key} data-testid="contact-attach-item" className={`flex items-center gap-2 text-sm rounded-lg px-3 py-2 ${f.status === 'error' ? 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400' : 'bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-200'}`}>
              {f.status === 'uploading' ? <Loader2 size={16} className="animate-spin shrink-0" /> : <FileText size={16} className="shrink-0" />}
              <span className="flex-1 truncate">{f.name}{f.status === 'error' && ` – ${f.error || t('contact.attachFailed')}`}</span>
              <button type="button" onClick={() => setFiles((prev) => prev.filter((x) => x.key !== f.key))} data-testid="contact-attach-remove" className="text-gray-400 hover:text-red-600">
                <X size={16} />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
